/**
 * AppErrorBoundary
 *
 * Top-level error boundary. Catches any uncaught render error in the app
 * tree, reports it to Sentry (via Sentry.ErrorBoundary), and shows a
 * branded fallback UI instead of a white screen.
 *
 * The Sentry ErrorBoundary handles the reporting automatically — we wrap
 * it only to inject PlanOS styling and reload / go-home affordances.
 *
 * Placement in main.jsx is OUTSIDE ThemeProvider so a crash inside the
 * theme layer still renders a usable fallback (hence the hardcoded color
 * fallbacks below: `var(--paper, #FAFAF7)` etc.).
 *
 * Router errorElement is a separate surface for route-level crashes —
 * this covers everything else (synchronous render errors, lifecycle
 * errors, uncaught throws from hooks during render).
 */
import { useEffect } from 'react'
import { Sentry } from '../lib/sentry'

// Mirror of the chunk-load detection in main.jsx — a stale dynamic import
// can fail late enough to bubble through Suspense and land here. We auto-
// reload once (30s guard, same key as main.jsx) so the planner doesn't
// have to click "Reload" themselves for a problem that's not their fault.
const CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to load module script/i,
  /Importing a module script failed/i,
  /Expected a JavaScript-or-Wasm module script/i,
]
function isChunkLoadError(err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = String(err.message || err)
  return CHUNK_PATTERNS.some(re => re.test(msg))
}

function FallbackUI({ error, eventId, resetError }) {
  const chunkError = isChunkLoadError(error)

  // If the boundary caught a chunk-load failure, auto-reload (same one-shot
  // guard as the global handler in main.jsx). The reload happens via
  // useEffect (after first paint), so we ALSO swap the visible copy below
  // for a calm "Updating…" message — without that swap the user sees an
  // alarming "Something went wrong." flash for one render frame during what
  // is actually just a deploy-staleness hiccup.
  useEffect(() => {
    if (!chunkError) return
    try {
      const last = Number(sessionStorage.getItem('planos.chunk_reload_at') || 0)
      if (Date.now() - last < 30_000) return
      sessionStorage.setItem('planos.chunk_reload_at', String(Date.now()))
    } catch { /* sessionStorage blocked — still try the reload */ }
    window.location.reload()
  }, [chunkError])

  // ── Chunk-load fallback ─────────────────────────────────────────────────
  // Common case during active ship cycles: a tab booted on bundle A clicks
  // a route whose chunk hash changed in bundle B. Reload fixes it; the
  // copy here just shouldn't read like the app broke.
  if (chunkError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        fontFamily: 'Inter, system-ui, sans-serif',
        background: 'var(--paper, #FAFAF7)',
        color: 'var(--ink, #1A1D23)',
      }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          {/* Subtle pulsing-halo dot — reuses planos-pulse keyframes from
              index.css (already used by the "you are here" indicator). The
              fallback ships outside ThemeProvider, but planos-pulse uses
              var(--accent) which defaults to the clay hex via the global
              :root fallback chain. Worst case (CSS not loaded yet) the
              dot still renders as a static accent-colored circle — no
              broken layout. */}
          <div
            aria-hidden="true"
            style={{
              width: 10, height: 10,
              borderRadius: '50%',
              background: 'var(--accent, #C65D45)',
              margin: '0 auto 22px',
              animation: 'planos-pulse 1.6s ease-in-out infinite',
            }}
          />
          <h1 style={{
            fontFamily: 'Fraunces, Cormorant Garamond, Georgia, serif',
            fontSize: 24, fontWeight: 400, letterSpacing: '-0.01em',
            marginBottom: 10,
            color: 'var(--ink, #1A1D23)',
          }}>
            Updating to the latest version…
          </h1>
          <p style={{
            color: 'var(--mute, #6b6e76)',
            lineHeight: 1.5,
            fontSize: 14,
            margin: 0,
          }}>
            This usually takes a second.
          </p>
        </div>
      </div>
    )
  }

  // ── Genuine-error fallback ──────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
      fontFamily: 'Inter, system-ui, sans-serif',
      background: 'var(--paper, #FAFAF7)',
      color: 'var(--ink, #1A1D23)',
    }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <h1 style={{
          fontFamily: 'Fraunces, Cormorant Garamond, Georgia, serif',
          fontSize: 28, fontWeight: 400, letterSpacing: '-0.01em',
          marginBottom: 12,
          color: 'var(--ink, #1A1D23)',
        }}>
          Something went wrong.
        </h1>
        <p style={{
          color: 'var(--mute, #6b6e76)',
          marginBottom: 20,
          lineHeight: 1.5,
          fontSize: 14,
        }}>
          PlanOS ran into an unexpected issue on this page. We've been
          notified automatically — a reload usually fixes it. If it keeps
          happening, include the reference below when you tell us.
        </p>
        {eventId && (
          <p style={{
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: 11,
            color: 'var(--mute, #6b6e76)',
            marginBottom: 24,
            wordBreak: 'break-all',
          }}>
            Reference: {eventId}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => { resetError(); window.location.reload() }}
            style={{
              background: 'var(--ink, #1A1D23)',
              color: 'var(--paper, #FAFAF7)',
              border: 'none', borderRadius: 999,
              padding: '10px 24px',
              fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => { resetError(); window.location.href = '/' }}
            style={{
              background: 'transparent',
              color: 'var(--ink, #1A1D23)',
              border: '1px solid var(--border, #e5e5e0)',
              borderRadius: 999,
              padding: '10px 24px',
              fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  )
}

export function AppErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary fallback={FallbackUI} showDialog={false}>
      {children}
    </Sentry.ErrorBoundary>
  )
}

export default AppErrorBoundary
